import { Injectable, inject } from '@angular/core';
import { SocketService, SOCKET_EVENTS, CLIENT_EVENTS } from './socket.service';
import { BrowserExtensionService } from './browser-extension.service';
import { Subject } from 'rxjs';

export interface BrowserAgentInterrupt {
    threadId: string;
    agentMetadataId: string;
    tool_call_id: string;
    type: string;
}

export interface BrowserAgentRequest {
    threadId: string;
    tool_call_id: string;
    message_id: string;
    type: 'get_state' | 'execute_actions';
    useVision?: boolean;
    tabId?: number;
    actions?: any[];
}

@Injectable({
    providedIn: 'root'
})
export class BrowserAgentService {
    private socketService = inject(SocketService);

    // Observable for browser agent requests
    private requestSubject = new Subject<BrowserAgentRequest>();
    public request$ = this.requestSubject.asObservable();

    constructor() {
        this.setupSocketListeners();
    }

    private setupSocketListeners(): void {

        // Listen for browser agent requests
        this.socketService.on<BrowserAgentRequest>(SOCKET_EVENTS.BROWSER_AGENT_REQUEST).subscribe(
            async (dataArray) => {
                // Socket service returns arrays, but Socket.IO emits single values
                const data = Array.isArray(dataArray) ? dataArray[0] : dataArray;

                // Forward request to extension
                try {
                    const response = await this.forwardRequestToExtension(data);
                    // Send response back to Node
                    this.sendResponse({
                        tool_call_id: data.tool_call_id,
                        message_id: data.message_id,
                        type: data.type === 'get_state' ? 'get_state_response' : 'execute_actions_response',
                        ...response
                    });
                } catch (error) {
                    this.sendResponse({
                        tool_call_id: data.tool_call_id,
                        message_id: data.message_id,
                        type: 'error',
                        error: error instanceof Error ? error.message : String(error)
                    });
                }
            }
        );
    }

    /**
     * Forward request to extension via content script
     */
    private async forwardRequestToExtension(request: BrowserAgentRequest): Promise<any> {
        return new Promise((resolve, reject) => {
            const messageId = `req_${Date.now()}_${Math.random()}`;

            // Set up listener for response
            const messageHandler = (event: MessageEvent) => {
                if (event.origin !== window.location.origin) {
                    return;
                }

                if (event.data && event.data.type === 'BROWSER_AGENT_RESPONSE' && event.data.messageId === messageId) {
                    window.removeEventListener('message', messageHandler);
                    if (event.data.error) {
                        reject(new Error(event.data.error));
                    } else {
                        resolve(event.data.response);
                    }
                }
            };

            window.addEventListener('message', messageHandler);

            // Send request to extension via content script
            window.postMessage({
                type: 'BROWSER_AGENT_REQUEST',
                messageId: messageId,
                payload: request
            }, window.location.origin);

            // Timeout after 60 seconds
            setTimeout(() => {
                window.removeEventListener('message', messageHandler);
                reject(new Error('Timeout waiting for extension response'));
            }, 60000);
        });
    }

    /**
     * Send response back to Node via socket
     */
    private sendResponse(response: {
        tool_call_id: string;
        message_id: string;
        type: string;
        state?: any;
        results?: any[];
        error?: string;
    }): void {
        this.socketService.getSocket().then(socket => {
            if (socket) {
                socket.emit(CLIENT_EVENTS.BROWSER_AGENT_RESPONSE, response);
            } else {
                console.error('[BrowserAgent] Socket not available');
            }
        });
    }
}

